<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import ProviderThumbnail from '@/components/Anonymization/ProviderThumbnail.vue'

// Anonymization cell: shows the anon providers for an IP. The first provider
// renders inline, followed by a "+N" affordance that opens a dropdown listing
// every provider. The whole-cell +/- (the cell's hover buttons) filters for/
// against ALL providers at once; individual per-provider filtering lives only
// in the "+N" dropdown (each row's + / -), OR-grouped in the host. Providers
// also carry data-shift-value so the shared cell-selection path can select one.
//
// vpn/proxy/residential fallbacks (no named service) are shown but not
// individually filterable — there's no provider string to match; use the
// whole-cell +/- for those rows instead.

const props = defineProps({
  data: { type: Object, default: null },
  field: { type: String, default: '' },
  filterable: { type: Boolean, default: false },
})

const emit = defineEmits(['provider-filter'])

const providers = computed(() => {
  const d = props.data
  if (!d) return []
  if (Array.isArray(d.services) && d.services.length > 0) {
    return d.services
      .map(s => ({
        label: s.display_name || s.provider,
        thumbnail: s.thumbnail_url,
        filterable: true,
      }))
      .filter(p => p.label)
  }
  const out = []
  if (d.vpn) out.push({ label: 'VPN (unknown service)', filterable: false })
  if (d.proxy) out.push({ label: 'Proxy (unknown service)', filterable: false })
  if (d.residential) out.push({ label: 'Residential proxy (unknown service)', filterable: false })
  return out
})

const primary = computed(() => providers.value[0] || null)
const rest = computed(() => providers.value.slice(1))
const hasThumb = computed(() => primary.value && primary.value.thumbnail !== undefined)

function emitFilter(label, negate) {
  emit('provider-filter', { value: label, negate })
  open.value = false
}

// --- overflow dropdown ---
const open = ref(false)
const triggerRef = ref(null)
const menuRef = ref(null)
const style = ref({})

async function toggleMenu() {
  if (open.value) {
    open.value = false
    return
  }
  open.value = true
  await nextTick()
  position()
}

function position() {
  if (!triggerRef.value || !menuRef.value) return
  const anchor = triggerRef.value.getBoundingClientRect()
  const rect = menuRef.value.getBoundingClientRect()
  let left = anchor.left
  let top = anchor.bottom + 4
  if (left + rect.width > window.innerWidth - 8) left = Math.max(8, window.innerWidth - rect.width - 8)
  if (top + rect.height > window.innerHeight - 8) top = Math.max(8, anchor.top - rect.height - 4)
  style.value = { top: `${top}px`, left: `${left}px` }
}

function onKeydown(e) {
  if (e.key === 'Escape' && open.value) open.value = false
}
function onClickOutside(e) {
  if (!open.value) return
  if (menuRef.value?.contains(e.target) || triggerRef.value?.contains(e.target)) return
  open.value = false
}

onMounted(() => {
  document.addEventListener('keydown', onKeydown)
  document.addEventListener('click', onClickOutside, true)
  window.addEventListener('resize', position)
  window.addEventListener('scroll', position, true)
})
onUnmounted(() => {
  document.removeEventListener('keydown', onKeydown)
  document.removeEventListener('click', onClickOutside, true)
  window.removeEventListener('resize', position)
  window.removeEventListener('scroll', position, true)
})
</script>

<template>
  <div class="anon-cell">
    <template v-if="primary">
      <span class="anon-chip" :data-shift-field="field" :data-shift-value="primary.label">
        <ProviderThumbnail
          v-if="hasThumb"
          :data="{ name: primary.label, thumbnail_url: primary.thumbnail }"
          class="anon-thumb"
        />
        <span class="anon-name">{{ primary.label }}</span>
      </span>

      <button
        v-if="rest.length > 0"
        ref="triggerRef"
        class="anon-more"
        :title="`Show all ${providers.length} providers`"
        @mousedown.stop
        @click.stop="toggleMenu"
      >
        +{{ rest.length }}
      </button>
    </template>

    <Teleport to="body">
      <div v-if="open" ref="menuRef" class="anon-menu" :style="style">
        <div v-for="(p, i) in providers" :key="i" class="anon-menu-row" :data-shift-field="field" :data-shift-value="p.label">
          <ProviderThumbnail
            v-if="p.thumbnail !== undefined"
            :data="{ name: p.label, thumbnail_url: p.thumbnail }"
            class="anon-thumb"
          />
          <span class="anon-menu-name" :title="p.label">{{ p.label }}</span>
          <span v-if="filterable && p.filterable" class="anon-actions">
            <button class="anon-btn anon-for" title="Filter for provider" @mousedown.stop @click.stop="emitFilter(p.label, false)">
              <font-awesome-icon :icon="['fas', 'plus']" />
            </button>
            <button class="anon-btn anon-out" title="Filter out provider" @mousedown.stop @click.stop="emitFilter(p.label, true)">
              <font-awesome-icon :icon="['fas', 'minus']" />
            </button>
          </span>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.anon-cell {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  min-width: 0;
}

.anon-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  min-width: 0;
}

.anon-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.anon-thumb {
  flex-shrink: 0;
}

:deep(.provider-thumbnail-container) {
  width: 16px;
  height: 16px;
}
:deep(.provider-thumbnail),
:deep(.provider-thumbnail-image) {
  width: 16px;
  height: 16px;
  font-size: 0.6rem;
}

.anon-actions {
  display: inline-flex;
  align-items: center;
  gap: 0.15rem;
  opacity: 0;
  transition: opacity var(--transition-fast, 0.15s) ease;
}

.anon-chip:hover .anon-actions,
.anon-menu-row:hover .anon-actions {
  opacity: 1;
}

.anon-more {
  flex-shrink: 0;
  padding: 0 0.35rem;
  font-size: 0.72rem;
  color: var(--text-muted);
  background: transparent;
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-xs);
  cursor: pointer;
}
.anon-more:hover {
  color: var(--accent-text);
  border-color: rgba(115, 87, 210, 0.4);
  background: var(--accent-soft-3);
}

.anon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1rem;
  height: 1rem;
  padding: 0;
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-xs);
  background: var(--surface-card);
  color: var(--text-faint);
  cursor: pointer;
  font-size: 0.5rem;
}
.anon-for:hover {
  background: var(--accent-soft-3);
  border-color: rgba(115, 87, 210, 0.4);
  color: var(--accent-text);
}
.anon-out:hover {
  background: var(--color-error-soft, rgba(239, 68, 68, 0.12));
  border-color: rgba(239, 68, 68, 0.4);
  color: var(--color-error);
}

.anon-menu {
  position: fixed;
  z-index: 1000;
  min-width: 12rem;
  max-width: 22rem;
  max-height: 18rem;
  overflow-y: auto;
  padding: 0.25rem;
  background: var(--surface-page);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg, 0 10px 40px rgba(0, 0, 0, 0.4));
}

.anon-menu-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.3rem 0.4rem;
  border-radius: var(--radius-sm);
}
.anon-menu-row:hover {
  background: var(--surface-hover);
}

.anon-menu-name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.8rem;
  color: var(--text-primary);
}
</style>
